import React, { useState, useCallback } from 'react';
import { XMarkIcon, DocumentArrowUpIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { BlobReader, ZipReader, TextWriter } from "@zip.js/zip.js";

// Helper to parse the session name from Header.csv in the .ACF file
const parseSessionNameFromFile = async (file) => {
  try {
    const zipReader = new ZipReader(new BlobReader(file));
    const entries = await zipReader.getEntries();
    const headerEntry = entries.find(e => e.filename.endsWith('Header.csv'));

    if (!headerEntry) {
      await zipReader.close();
      return file.name.replace(/\.(zip|acf)$/i, '');
    }

    const headerContent = await headerEntry.getData(new TextWriter());
    await zipReader.close();
    
    // Try to find Job name first
    const jobLine = headerContent.split('\n').find(line => line.trim().startsWith('Job ='));
    if (jobLine && jobLine.split('=')[1] && jobLine.split('=')[1].trim()) {
      return jobLine.split('=')[1].trim();
    }
    
    // Fallback to Project name
    const projectLine = headerContent.split('\n').find(line => line.trim().startsWith('Project ='));
    if (projectLine && projectLine.split('=')[1] && projectLine.split('=')[1].trim()) {
      return projectLine.split('=')[1].trim();
    }

    return file.name.replace(/\.(zip|acf)$/i, '');
  } catch (error) {
    console.error("Failed to parse session name from file:", error);
    return file.name.replace(/\.(zip|acf)$/i, '');
  }
};

export default function UploadModal({ 
  isOpen, 
  onClose, 
  projectId, 
  projectName,
  folderId = null,
  folderName = null,
  onStartUpload 
}) {
  const [file, setFile] = useState(null);
  const [sessionName, setSessionName] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [isParsing, setIsParsing] = useState(false);

  // Handle file selection and parse for session name
  const handleFileChange = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    
    setFile(selectedFile);
    setIsParsing(true);
    
    try {
      const suggestedName = await parseSessionNameFromFile(selectedFile);
      setSessionName(suggestedName);
    } catch (err) {
      console.error('Failed to parse file:', err);
      setSessionName(selectedFile.name.replace(/\.(zip|acf)$/i, ''));
    } finally {
      setIsParsing(false);
    }
  }, []);

  // Drag and drop handlers
  const handleDragEnter = (e) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsDragging(true); 
  };
  
  const handleDragLeave = (e) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
    setIsDragging(false); 
  };
  
  const handleDragOver = (e) => { 
    e.preventDefault(); 
    e.stopPropagation(); 
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleUpload = () => {
    if (!file) return;
    
    // Hand off to global upload handler (runs in background)
    onStartUpload({
      file,
      projectId,
      folderId,
      sessionName: sessionName.trim() || file.name.replace(/\.(zip|acf)$/i, '')
    });
    
    // Reset and close immediately
    setFile(null);
    setSessionName('');
    onClose();
  };

  const handleClose = () => {
    setFile(null);
    setSessionName('');
    setIsDragging(false);
    onClose();
  };

  // Build the location breadcrumb text
  const locationText = folderName 
    ? `${projectName} / ${folderName}`
    : projectName;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Upload Session</h3>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center">
              <span className="truncate max-w-xs" title={locationText}>
                to {locationText}
              </span>
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {!file ? (
            // Drop zone
            <label 
              htmlFor="modal-file-upload"
              className={`block p-8 text-center border-2 border-dashed rounded-sm transition-all duration-200 cursor-pointer ${
                isDragging 
                  ? 'border-red-400 bg-red-50' 
                  : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100'
              }`}
              onDragEnter={handleDragEnter} 
              onDragLeave={handleDragLeave} 
              onDragOver={handleDragOver} 
              onDrop={handleDrop}
            >
              <input 
                id="modal-file-upload" 
                type="file" 
                className="sr-only" 
                accept=".zip,.acf,application/zip,application/x-zip-compressed"
                onChange={(e) => handleFileChange(e.target.files[0])}
              />
              <DocumentArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
              <p className="mt-3 text-sm font-medium text-gray-600">
                Drop your .ACF file here
              </p>
              <p className="mt-1 text-xs text-gray-500">
                or click to browse
              </p>
            </label>
          ) : (
            // File selected state
            <div className="space-y-4">
              {/* File info */}
              <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-sm">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                  <p className="text-xs text-gray-500">
                    {(file.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                </div>
                <button 
                  onClick={() => { setFile(null); setSessionName(''); }}
                  className="text-sm text-gray-500 hover:text-gray-700 ml-3"
                >
                  Change
                </button>
              </div>

              {/* Session name input */}
              <div>
                <label htmlFor="sessionName" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Session Name
                </label>
                <input
                  type="text"
                  id="sessionName"
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  disabled={isParsing}
                  className="block w-full px-3 py-2 rounded-sm border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm disabled:bg-gray-100"
                  placeholder="e.g., Lane 1 - Northbound"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {isParsing ? 'Reading file metadata...' : 'Auto-detected from file metadata'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
          <button 
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button 
            onClick={handleUpload}
            disabled={!file || isParsing}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
}